import { Link, useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { PageHeader } from '../components/Layout';
import { Card, SectionTitle, StageBadge, Spinner, ErrorNote } from '../components/ui';
import { money, fmtDate } from '../lib/format';
import { PIPELINE_ORDER, stageLabel } from '../lib/stages';
import { roofTypeLabel } from '../lib/roofTypes';

/** Every step is tappable so you can jump anywhere in the trail. */
function Timeline({ p }) {
  const navigate = useNavigate();
  const steps = [
    { key: 'new_lead', label: 'Lead logged', detail: fmtDate(p.createdAt), to: null },
    {
      key: 'visit_scheduled', label: 'Site visit', to: `/project/${p.id}/visit`,
      detail: p.siteVisit
        ? (p.siteVisit.completedAt ? `Completed ${fmtDate(p.siteVisit.completedAt)}` : `Scheduled ${fmtDate(p.siteVisit.scheduledAt)}`)
        : 'Not scheduled',
    },
    // One step: the customer signs the proposal itself, which is the agreement.
    {
      key: 'quote_sent', label: 'Proposal & signing', to: `/project/${p.id}/proposal`,
      detail: !p.proposal ? 'Not created'
        : p.agreement?.status === 'signed'
          ? `#${p.proposal.number} · ${money(p.proposal.total)} · Signed ${fmtDate(p.agreement.signedAt)}`
          : `#${p.proposal.number} · ${money(p.proposal.total)} · ${
              p.proposal.status === 'sent' ? 'Sent — awaiting signature'
                : p.proposal.status === 'rejected' ? stageLabel('quote_rejected') : 'Draft'}`,
    },
    {
      key: 'job_in_progress', label: 'Job', to: `/project/${p.id}/job`,
      detail: p.job
        ? (p.job.completedAt ? `Completed ${fmtDate(p.job.completedAt)}`
          : p.job.startedAt ? `In progress · ${p.job.progressUpdates?.length || 0} updates`
          : 'Not started')
        : 'Not created',
    },
    {
      key: 'invoice_sent', label: 'Invoice', to: `/project/${p.id}/invoice`,
      detail: p.invoice ? `#${p.invoice.number} · ${money(p.invoice.total)} · ${p.invoice.status}` : 'Not sent',
    },
  ];

  const currentIdx = PIPELINE_ORDER.indexOf(p.stage);

  return (
    <ol className="timeline" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {steps.map((s, i) => {
        const idx = PIPELINE_ORDER.indexOf(s.key);
        const done = idx < currentIdx;
        const current = idx === currentIdx || (s.key === 'new_lead' && i === 0);
        const cls = `timeline-step ${current ? 'on' : ''} ${done ? 'dn' : ''} ${s.to ? 'nav' : ''}`;
        const inner = (
          <>
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <span className="dot">{done ? '✓' : i + 1}</span>
              {i < steps.length - 1 ? <span className={`timeline-line ${done ? 'dn' : current ? 'on' : ''}`} /> : null}
            </span>
            <span className="body">
              {s.label}
              <span className="sb" style={{ display: 'block', fontWeight: 400 }}>{s.detail}</span>
            </span>
            {s.to ? <span className="sb" aria-hidden="true">›</span> : null}
          </>
        );
        return (
          <li key={s.key}>
            {s.to
              ? <button className={cls} onClick={() => navigate(s.to)}>{inner}</button>
              : <div className={cls}>{inner}</div>}
          </li>
        );
      })}
    </ol>
  );
}

/** Which next step makes sense given where the project actually is. */
function actionsFor(p) {
  const out = [];
  const base = `/project/${p.id}`;

  out.push(!p.siteVisit || !p.siteVisit.completedAt
    ? { to: `${base}/visit`, label: 'Record site visit', pri: true }
    : { to: `${base}/visit`, label: 'Edit site visit' });

  if (!p.proposal || p.proposal.status === 'draft') {
    out.push({ to: `${base}/proposal`, label: p.proposal ? 'Edit & send proposal' : 'Create proposal', pri: !!p.siteVisit });
  } else if (p.proposal.status === 'sent') {
    out.push({ href: `/p/${p.proposal.token}`, label: 'View proposal link' });
  }

  if (p.agreement?.status === 'signed' && !p.job) out.push({ to: `${base}/job`, label: 'Start job', pri: true });

  if (p.job) out.push({ to: `${base}/job`, label: p.job.completedAt ? 'View job log' : 'Update job progress', pri: !p.job.completedAt });

  if (p.job?.completedAt && (!p.invoice || p.invoice.status === 'draft')) {
    out.push({ to: `${base}/invoice`, label: 'Create invoice', pri: true });
  }
  if (p.invoice && p.invoice.status !== 'draft') {
    out.push({ to: `${base}/invoice`, label: 'View / record payment' });
    out.push({ href: `/inv/${p.invoice.token}`, label: 'View public invoice' });
  }
  out.push({ to: `/email/new?project=${p.id}`, label: 'Send email / follow-up' });
  return out;
}

export default function ProjectDetail() {
  const { id } = useParams();
  const { data: p, error, loading, reload } = useAsync(() => api.get('/projects/' + id), [id]);

  if (loading) return <Spinner label="Loading project" />;
  if (error) return <div className="view"><ErrorNote onRetry={reload}>{error}</ErrorNote></div>;
  if (!p) return null;

  return (
    <>
      <PageHeader title={p.customer?.name || 'Project'} sub={`${p.number} · ${p.title || ''}`} back="/pipeline" />
      <div className="view">
        <div className="split">
          <Card>
            <div className="project-summary">
              <div><div className="sb">Stage</div><StageBadge stage={p.stage} /></div>
              <div><div className="sb">Roof type</div><div className="nm">{roofTypeLabel(p.roofType)}</div></div>
              <div style={{ textAlign: 'right' }}><div className="sb">Created</div><div className="nm">{fmtDate(p.createdAt)}</div></div>
            </div>
            {p.notes ? <p className="sb" style={{ marginTop: 'var(--s-3)', lineHeight: 'var(--lh-relaxed)' }}>{p.notes}</p> : null}
          </Card>

          <Card>
            <SectionTitle>Quick actions</SectionTitle>
            <div className="col" style={{ gap: 'var(--s-2)', marginTop: 'var(--s-3)' }}>
              {actionsFor(p).map((a, i) => a.href ? (
                <a key={i} className={`btn ${a.pri ? 'pri' : ''}`} href={a.href} target="_blank" rel="noopener noreferrer">{a.label}</a>
              ) : (
                <Link key={i} className={`btn ${a.pri ? 'pri' : ''}`} to={a.to}>{a.label}</Link>
              ))}
            </div>
          </Card>

          <Card tight className="span-all">
            <SectionTitle>Timeline</SectionTitle>
            <Timeline p={p} />
          </Card>
        </div>

        <Link className="btn" to={`/customer/${p.customerId}`}>View customer profile</Link>
      </div>
    </>
  );
}
